import { useCallback, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { AuthContext } from "../../context/AuthContext";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { useKillSwitch } from "../../hooks/api/admin/useKillSwitch";
import { SPACING } from "../../constants/design/spacing";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";

const Controls = styled.section<{ tripped: boolean }>`
  padding: ${SPACING.two}px ${SPACING.three}px;
  background: ${({ tripped }) =>
    tripped ? COLORS.red.five : BACKGROUND_COLORS.componentSolid};
  border-bottom: 1px solid ${BORDER_COLORS.one};
  min-width: 0;
  color: ${TEXT_COLORS.one};
  button:focus-visible,
  input:focus-visible {
    outline: 2px solid #32cce3;
    outline-offset: 2px;
  }
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.two}px;
  min-height: 34px;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.two}px;
  margin-left: auto;
  @media (max-width: 700px) {
    margin-left: 0;
    width: 100%;
  }
`;
const Note = styled.div`
  color: ${TEXT_COLORS.two};
  margin-top: ${SPACING.one}px;
  overflow-wrap: anywhere;
  ${FONT_STYLE.label.two}
`;
const fmtUsd = (raw?: string) => {
  if (!raw) return "—";
  const n = Number(raw);
  return Number.isFinite(n)
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : raw;
};

export function KillSwitchControls() {
  const {
    data,
    error,
    isLoading,
    isSignedIn,
    canReset,
    canEnablePolicy,
    awaitingPolicy,
    enableResetApprovals,
    isMutating,
    mutate,
    resetDailyKillSwitch,
    setDailyKillSwitchThreshold,
  } = useKillSwitch();
  const [thresholdInput, setThresholdInput] = useState("");
  const [notice, setNotice] = useState("");
  const { tokenId } = useContext(AuthContext);
  useEffect(() => setNotice(""), [data?.trip_id, tokenId]);
  const protectedReset = Boolean(
    data?.reset_policy_enabled &&
      data?.tripped &&
      data?.required_approvals === 2
  );
  const approvals = data?.approvals ?? [];
  const unavailable =
    !isSignedIn || !data || Boolean(error) || isLoading || isMutating;
  const resetDisabled = unavailable || !canReset;

  const onReset = useCallback(async () => {
    let message =
      "Reset the kill switch?\n\nThis clears current usage. Flagged withdrawals will still need separate review.";
    if (protectedReset) {
      message =
        approvals.length === 0
          ? "Approve this reset?\n\nThe kill switch will stay on until a second staff member approves."
          : "Give the second approval and reset the kill switch?\n\nFlagged withdrawals will still need separate review.";
    }
    if (!window.confirm(message)) return;
    setNotice("");
    const outcome = await resetDailyKillSwitch();
    if (outcome === "reset")
      setNotice(
        "Reset complete. Review flagged withdrawals separately; their manual-review status is unchanged."
      );
    if (outcome === "approval_recorded")
      setNotice(
        "Approval recorded. Waiting for another staff member; the kill switch is still on."
      );
  }, [protectedReset, approvals.length, resetDailyKillSwitch]);

  const onSetThreshold = useCallback(async () => {
    const value = Number(thresholdInput);
    if (!Number.isFinite(value) || value <= 0) {
      setNotice("Enter a threshold greater than zero.");
      return;
    }
    if (
      !window.confirm(
        `Set the kill-switch threshold to $${value.toLocaleString()}?\n\nLowering it below current usage can trip the switch. ${
          protectedReset
            ? "This trip still requires two reset approvals."
            : "Raising it above current usage can clear the switch."
        }`
      )
    )
      return;
    if (await setDailyKillSwitchThreshold(value)) setThresholdInput("");
  }, [thresholdInput, protectedReset, setDailyKillSwitchThreshold]);

  const onEnablePolicy = useCallback(async () => {
    if (
      !window.confirm(
        "Enable two-person kill-switch resets?\n\nEvery fourth activation will require two different authorized staff members to approve the reset. Daily usage resets will not clear the activation count. All backend consumers must be updated first. This policy cannot be disabled through admin. Enabling it does not reset the switch or release flagged withdrawals. An already-tripped switch starts at activation 1; otherwise the count starts at 0."
      )
    )
      return;
    await enableResetApprovals();
  }, [enableResetApprovals]);

  let label = data?.tripped ? "TRIPPED" : "OK";
  if (!isSignedIn) label = "Sign in required";
  else if (error) label = "Status unavailable";
  else if (isLoading || !data) label = "Loading…";

  let resetLabel = "Reset";
  if (protectedReset)
    resetLabel = approvals.length ? "Approve & reset" : "Approve reset";
  if (data?.has_approved) resetLabel = "Approved by you";
  if (isMutating) resetLabel = "Saving…";

  return (
    <Controls
      tripped={Boolean(data?.tripped)}
      aria-label="Kill switch controls"
    >
      <Row>
        <span style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two }}>
          Kill switch
        </span>
        <strong
          style={{
            ...FONT_STYLE.label.one,
            color: data?.tripped ? COLORS.red.one : TEXT_COLORS.one,
          }}
        >
          {label}
        </strong>
        {data && (
          <span style={FONT_STYLE.label.two}>
            {fmtUsd(data.used)} / {fmtUsd(data.threshold)}
          </span>
        )}
        {data?.reset_policy_enabled && (
          <span style={{ ...FONT_STYLE.label.two, color: TEXT_COLORS.two }}>
            Trips: {data.activation_count ?? 0} / 4
          </span>
        )}
        <Actions>
          <input
            aria-label="New kill-switch threshold in USD"
            name="threshold"
            type="number"
            min="0"
            step="any"
            placeholder="New threshold (USD)"
            value={thresholdInput}
            disabled={unavailable || protectedReset}
            onChange={(e) => setThresholdInput(e.target.value)}
            style={{
              width: 180,
              maxWidth: "100%",
              height: 30,
              padding: `0 ${SPACING.two}px`,
              borderRadius: 4,
              border: `1px solid ${BORDER_COLORS.one}`,
              background: BACKGROUND_COLORS.component,
              color: TEXT_COLORS.one,
            }}
          />
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={!thresholdInput || unavailable || protectedReset}
            onClick={onSetThreshold}
            style={{ height: 30, padding: `0 ${SPACING.two}px` }}
          >
            Set threshold
          </Button>
          <Button
            buttonTheme={ButtonThemeEnum.NEGATIVE}
            disabled={resetDisabled}
            onClick={onReset}
            style={{ height: 30, padding: `0 ${SPACING.two}px` }}
          >
            {resetLabel}
          </Button>
        </Actions>
      </Row>
      {data?.reset_policy_enabled === false && (
        <Note>
          <span>
            Two-person reset policy is off. WRITE_SYSTEM and WRITE_CONFIG
            permissions are required to enable it.{" "}
          </span>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={unavailable || !canEnablePolicy || awaitingPolicy}
            onClick={onEnablePolicy}
          >
            {awaitingPolicy
              ? "Waiting for activation…"
              : "Enable two-person resets"}
          </Button>
          {awaitingPolicy && (
            <Button
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={() => {
                mutate();
              }}
            >
              Refresh status
            </Button>
          )}
        </Note>
      )}
      {error && isSignedIn && (
        <Note role="alert">
          Could not load the current status.{" "}
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={() => {
              mutate();
            }}
          >
            Retry
          </Button>
        </Note>
      )}
      {protectedReset && (
        <Note>
          <strong>{approvals.length} / 2 approvals</strong> ·{" "}
          {data?.has_approved
            ? "Waiting for another staff member."
            : "Two different staff members must approve this reset."}
          {approvals.length > 0 && (
            <span>
              {" "}
              Approved by {approvals.map((a) => a.email).join(", ")}.
            </span>
          )}
          <span> Threshold changes are locked until reset.</span>
        </Note>
      )}
      {data?.reset_policy_enabled && !protectedReset && (
        <Note>
          Every fourth trip needs two reset approvals. Daily usage resets do not
          clear this count.
        </Note>
      )}
      {notice && isSignedIn && <Note role="status">{notice}</Note>}
    </Controls>
  );
}
